import { useEffect, useRef, useState } from 'react'

interface RecognitionResult {
  showcaseId: string | null
  confidence: number
  alternatives: { showcaseId: string; confidence: number }[]
  /** Was die Stufen gesehen haben — Modellantwort, Anzahl der Referenzfotos. */
  debug?: string
}

/**
 * SPEC: "Referenzbilder ... direkt mit der Handykamera testen, ob die Erkennung
 * greift" (Admin). Läuft genau wie für Besucher: erst das kleine Modell mit
 * den visuellen Merkmalen, dann — gründlich — das große mit den Referenzfotos.
 * Die Zeile darunter sagt, welche Stufe entschieden hat und was das Modell
 * geantwortet hat; sonst sieht „nichts erkannt" immer gleich aus.
 */
export function CameraTest({ showcaseTitleById }: { showcaseTitleById: Map<string, string> }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [active, setActive] = useState(false)
  const [result, setResult] = useState<RecognitionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [gruendlich, setGruendlich] = useState(true)

  useEffect(() => {
    return () => {
      const stream = videoRef.current?.srcObject as MediaStream | null
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  async function start() {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      if (videoRef.current) videoRef.current.srcObject = stream
      setActive(true)
    } catch {
      setError('Kamerazugriff nicht möglich.')
    }
  }

  function stop() {
    const stream = videoRef.current?.srcObject as MediaStream | null
    stream?.getTracks().forEach((t) => t.stop())
    setActive(false)
    setResult(null)
  }

  async function captureAndTest() {
    if (!videoRef.current) return
    setBusy(true)
    setError(null)
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = (512 * videoRef.current.videoHeight) / videoRef.current.videoWidth
      canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85)

      // Über das Admin (Access), nicht über die Besucher-Route — die verlangt
      // ein Sitzungs-Cookie, das das Admin nicht hat.
      const res = await fetch('/api/v1/admin/recognition/test', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageDataUrl, thorough: gruendlich, stage2: 'strong' }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text.slice(0, 120)}`)
      }
      setResult(await res.json())
    } catch (err) {
      setError(`Erkennung fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card p-5">
      <h3 className="mb-2 font-medium">Mit Kamera testen</h3>
      {!active ? (
        <button onClick={() => void start()} className="btn-primary">
          Kamera starten
        </button>
      ) : (
        <div className="space-y-3">
          <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-sm rounded-lg" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => void captureAndTest()} disabled={busy} className="btn-primary">
              {busy ? 'Prüft …' : 'Standbild prüfen'}
            </button>
            <button onClick={stop} className="btn-danger">
              Kamera stoppen
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={gruendlich}
              onChange={(e) => setGruendlich(e.target.checked)}
            />
            Gründlich: Erkennt das kleine Modell nichts, schaut das große mit den Referenzfotos
          </label>
          {result && (
            <div className="rounded-lg bg-bg-elevated p-3 text-sm">
              {result.showcaseId ? (
                <p>
                  Erkannt:{' '}
                  <strong>{showcaseTitleById.get(result.showcaseId) ?? result.showcaseId}</strong> (
                  {Math.round(result.confidence * 100)}% Konfidenz)
                </p>
              ) : (
                <p>Kein Showcase mit ausreichender Konfidenz erkannt.</p>
              )}
              {result.alternatives.length > 0 && (
                <p className="mt-1 text-text-muted">
                  Alternativen:{' '}
                  {result.alternatives
                    .map((a) => showcaseTitleById.get(a.showcaseId) ?? a.showcaseId)
                    .join(', ')}
                </p>
              )}
              {result.debug && (
                <p className="mt-2 break-words font-mono text-xs text-text-muted">{result.debug}</p>
              )}
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-2 text-status-error">{error}</p>}
    </div>
  )
}
